import { useCallback, useEffect, useReducer, useRef } from 'react';

export type HeroVideoPlaybackState = 'loading' | 'playing';
export type HeroVideoPlaybackEvent =
  | 'emptied'
  | 'error'
  | 'pause'
  | 'playing'
  | 'stalled'
  | 'waiting';

export function heroVideoPlaybackReducer(
  _state: HeroVideoPlaybackState,
  event: HeroVideoPlaybackEvent,
): HeroVideoPlaybackState {
  return event === 'playing' ? 'playing' : 'loading';
}

type HeroVideoSnapshot = {
  ended: boolean;
  paused: boolean;
  readyState: number;
};

export function resolveHeroVideoPlaybackState({
  ended,
  paused,
  readyState,
}: HeroVideoSnapshot): HeroVideoPlaybackState {
  return !paused && !ended && readyState >= 2 ? 'playing' : 'loading';
}

function reportPlaybackError(error: unknown) {
  if (error instanceof DOMException && error.name === 'AbortError') {
    return;
  }

  console.error('Hero video playback failed', error);
}

export function useHeroVideoPlayback() {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [playbackState, dispatch] = useReducer(
    heroVideoPlaybackReducer,
    'loading',
  );

  const attemptPlayback = useCallback(() => {
    const video = videoRef.current;
    if (!video) {
      return;
    }

    const currentState = resolveHeroVideoPlaybackState(video);
    if (currentState === 'playing') {
      dispatch('playing');
      return;
    }

    if (document.visibilityState === 'hidden') {
      return;
    }

    void video.play().catch((error) => {
      dispatch('error');
      reportPlaybackError(error);
    });
  }, []);

  useEffect(() => {
    const retryWhenVisible = () => {
      if (document.visibilityState === 'visible') {
        attemptPlayback();
      }
    };

    window.addEventListener('pageshow', attemptPlayback);
    window.addEventListener('pointerdown', attemptPlayback, { passive: true });
    document.addEventListener('visibilitychange', retryWhenVisible);
    attemptPlayback();

    return () => {
      window.removeEventListener('pageshow', attemptPlayback);
      window.removeEventListener('pointerdown', attemptPlayback);
      document.removeEventListener('visibilitychange', retryWhenVisible);
    };
  }, [attemptPlayback]);

  return {
    attemptPlayback,
    playbackState,
    setEmptied: () => dispatch('emptied'),
    setError: () => dispatch('error'),
    setPaused: () => dispatch('pause'),
    setPlaying: () => dispatch('playing'),
    setStalled: () => dispatch('stalled'),
    setWaiting: () => dispatch('waiting'),
    videoRef,
  };
}
